import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  ShieldAlert, UserPlus, Trash2, Edit, ToggleLeft, ToggleRight, Loader2,
  Eye, EyeOff, Euro, Plus, Minus, Wallet, History,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', risk_access: false });
  const [balances, setBalances] = useState({});
  const [creditDialog, setCreditDialog] = useState(null); // { email, name, action: 'topup'|'deduct' }
  const [creditAmount, setCreditAmount] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [creditLoading, setCreditLoading] = useState(false);
  const [txnDialog, setTxnDialog] = useState(null); // { email, name, transactions }

  useEffect(() => { fetchAdmins(); fetchBalances(); }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get('/admin/admin-users');
      setAdmins(res.data);
    } catch { toast.error('Errore nel caricamento admin'); }
    setLoading(false);
  };

  const fetchBalances = async () => {
    try {
      const res = await adminApi.get('/admin/risk/wallet/all-balances');
      const map = {};
      (res.data.admins || []).forEach(a => { map[a.email] = a.balance; });
      setBalances(map);
    } catch { /* ignore */ }
  };

  const resetForm = () => {
    setForm({ name: '', email: '', password: '', risk_access: false });
    setShowForm(false);
    setEditingId(null);
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    try {
      await adminApi.post('/admin/admin-users', form);
      toast.success('Admin creato con successo');
      resetForm();
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore nella creazione'); }
  };

  const handleUpdate = async () => {
    const data = { name: form.name, risk_access: form.risk_access };
    if (form.password) data.password = form.password;
    try {
      await adminApi.put(`/admin/admin-users/${editingId}`, data);
      toast.success('Admin aggiornato');
      resetForm();
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id, email) => {
    if (!confirm(`Eliminare l'admin ${email}?`)) return;
    try {
      await adminApi.delete(`/admin/admin-users/${id}`);
      toast.success('Admin eliminato');
      fetchAdmins();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const startEdit = (admin) => {
    setForm({ name: admin.name, email: admin.email, password: '', risk_access: admin.risk_access });
    setEditingId(admin.id);
    setShowForm(true);
  };

  const handleCreditAction = async () => {
    const amount = parseFloat(creditAmount);
    if (!amount || amount <= 0) return toast.error('Importo non valido');
    setCreditLoading(true);
    try {
      const endpoint = creditDialog.action === 'topup' ? '/admin/risk/wallet/admin-topup' : '/admin/risk/wallet/admin-deduct';
      const { data } = await adminApi.post(endpoint, {
        email: creditDialog.email,
        amount,
        note: creditNote || (creditDialog.action === 'topup' ? 'Ricarica credito' : 'Sottrazione credito'),
      });
      toast.success(data.message);
      setCreditDialog(null);
      setCreditAmount('');
      setCreditNote('');
      fetchBalances();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setCreditLoading(false);
    }
  };

  const handleViewTransactions = async (email, name) => {
    try {
      const { data } = await adminApi.get(`/admin/risk/wallet/admin-transactions/${email}`);
      setTxnDialog({ email, name, transactions: data.transactions || [] });
    } catch {
      toast.error('Errore nel caricamento transazioni');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
            <ShieldAlert className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Gestione Admin</h1>
            <p className="text-sm text-slate-500">Crea e gestisci gli account amministratore e il loro credito</p>
          </div>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(!showForm); }} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-admin-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Nuovo Admin
        </Button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="admin-form">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            {editingId ? 'Modifica Admin' : 'Crea Nuovo Admin'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-600">Nome Completo *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Mario Rossi" className="mt-1" data-testid="admin-form-name" />
            </div>
            <div>
              <Label className="text-sm text-slate-600">Email *</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="mario@example.com" className="mt-1" disabled={!!editingId} data-testid="admin-form-email" />
            </div>
            <div>
              <Label className="text-sm text-slate-600">{editingId ? 'Nuova Password (lascia vuoto per non cambiare)' : 'Password *'}</Label>
              <div className="relative mt-1">
                <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min. 6 caratteri" className="pr-10" data-testid="admin-form-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer p-2">
                <input type="checkbox" checked={form.risk_access} onChange={e => setForm(f => ({ ...f, risk_access: e.target.checked }))} className="w-4 h-4 rounded" data-testid="admin-form-risk" />
                <span className="text-sm text-slate-700">Abilita Risk Intelligence</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <Button onClick={editingId ? handleUpdate : handleCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="admin-form-submit">
              {editingId ? 'Aggiorna' : 'Crea Admin'}
            </Button>
            <Button variant="outline" onClick={resetForm}>Annulla</Button>
          </div>
        </div>
      )}

      {/* Admin List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Admin</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Email</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Credito</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Risk</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a, i) => (
                <tr key={a.id || a.email} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`} data-testid={`admin-row-${a.email}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${a.is_super_admin ? 'bg-gradient-to-br from-teal-500 to-cyan-600' : 'bg-indigo-500'}`}>
                        {a.name?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                      <span className="text-sm font-medium text-slate-800">{a.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">{a.email}</td>
                  <td className="py-3 px-4 text-center">
                    {a.is_super_admin ? (
                      <Badge className="bg-teal-100 text-teal-700">Super Admin</Badge>
                    ) : a.source === 'system' ? (
                      <Badge className="bg-indigo-100 text-indigo-700">Sistema</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600">Standard</Badge>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {a.is_super_admin ? (
                      <span className="text-xs text-teal-600 font-medium">Illimitato</span>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <span className={`text-sm font-bold ${(balances[a.email] || 0) > 0 ? 'text-emerald-600' : 'text-red-500'}`} data-testid={`admin-balance-${a.email}`}>
                          {(balances[a.email] || 0).toFixed(2)}
                        </span>
                        <Euro className="w-3 h-3 text-slate-400" />
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {a.is_super_admin ? (
                      <span className="text-xs text-teal-600 font-medium">Illimitato</span>
                    ) : a.risk_access ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium"><ToggleRight className="w-4 h-4" /></span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400"><ToggleLeft className="w-4 h-4" /></span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      {!a.is_super_admin && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => { setCreditDialog({ email: a.email, name: a.name, action: 'topup' }); setCreditAmount(''); setCreditNote(''); }} title="Ricarica credito" data-testid={`topup-admin-${a.email}`}>
                            <Plus className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setCreditDialog({ email: a.email, name: a.name, action: 'deduct' }); setCreditAmount(''); setCreditNote(''); }} title="Sottrai credito" data-testid={`deduct-admin-${a.email}`}>
                            <Minus className="w-4 h-4 text-red-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleViewTransactions(a.email, a.name)} title="Storico transazioni" data-testid={`txn-admin-${a.email}`}>
                            <History className="w-4 h-4 text-slate-500" />
                          </Button>
                        </>
                      )}
                      {a.source === 'created' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => startEdit(a)} data-testid={`edit-admin-${a.id}`}>
                            <Edit className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id, a.email)} data-testid={`delete-admin-${a.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                      {a.source === 'system' && !a.is_super_admin && (
                        <Button variant="ghost" size="sm" onClick={() => startEdit(a)} data-testid={`edit-admin-${a.id}`}>
                          <Edit className="w-4 h-4 text-slate-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Credit Dialog */}
      <Dialog open={!!creditDialog} onOpenChange={() => setCreditDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-indigo-600" />
              {creditDialog?.action === 'topup' ? 'Ricarica Credito' : 'Sottrai Credito'}
            </DialogTitle>
            <DialogDescription>
              {creditDialog?.action === 'topup' ? 'Aggiungi' : 'Sottrai'} credito per <strong>{creditDialog?.name}</strong> ({creditDialog?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Importo *</Label>
              <div className="relative mt-1">
                <Input
                  type="number" min="0.01" step="0.01"
                  value={creditAmount}
                  onChange={e => setCreditAmount(e.target.value)}
                  placeholder="10.00"
                  data-testid="credit-amount-input"
                />
                <Euro className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
            </div>
            <div>
              <Label>Nota (opzionale)</Label>
              <Input value={creditNote} onChange={e => setCreditNote(e.target.value)} placeholder="es. Ricarica mensile" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditDialog(null)}>Annulla</Button>
            <Button
              onClick={handleCreditAction}
              disabled={creditLoading}
              className={creditDialog?.action === 'topup' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="credit-confirm-btn"
            >
              {creditLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : creditDialog?.action === 'topup' ? <Plus className="w-4 h-4 mr-1" /> : <Minus className="w-4 h-4 mr-1" />}
              {creditDialog?.action === 'topup' ? 'Ricarica' : 'Sottrai'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transactions Dialog */}
      <Dialog open={!!txnDialog} onOpenChange={() => setTxnDialog(null)}>
        <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600" />
              Transazioni — {txnDialog?.name}
            </DialogTitle>
            <DialogDescription>{txnDialog?.email}</DialogDescription>
          </DialogHeader>
          {txnDialog?.transactions?.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Nessuna transazione</p>
          ) : (
            <div className="divide-y max-h-[50vh] overflow-y-auto">
              {txnDialog?.transactions?.map((t, i) => (
                <div key={i} className="flex items-center justify-between py-3 text-sm">
                  <div>
                    <p className="font-medium text-slate-800">
                      {t.type === 'topup' ? 'Ricarica' : t.type === 'deduct' ? 'Sottrazione' : t.report_label || 'Addebito'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {t.note || t.report_label || ''} {t.created_by ? `• da ${t.created_by}` : ''}
                    </p>
                    <p className="text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleString('it-IT') : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${t.amount >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {t.amount >= 0 ? '+' : ''}{t.amount?.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-400">Saldo: {t.balance_after?.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManagement;
